/**
 * Contrato da API de gestão (`/api/v1`).
 *
 * Transcrição literal do manual — os nomes são os do servidor, sem tradução e
 * sem camelCase inventado. Toda divergência entre este arquivo e a resposta real
 * é bug daqui, não da tela que consome.
 */

import type { AgentCommand } from './admin.ts'

// ── Erro ────────────────────────────────────────────────────────────────────

/**
 * RFC 9457. Dois formatos convivem: o do Athena, com a extensão `code`, e o do
 * binding do ASP.NET, com `errors` e sem `code`. Quem trata erro precisa dos
 * dois — programar em cima de `detail` é que não é opção, ele é texto humano.
 */
export interface Problem {
  status: number
  title: string
  type?: string
  detail?: string
  traceId?: string
  /** Código de negócio, snake_case, estável. É o contrato. */
  code?: string
  /** Erro de binding do framework: campo → mensagens. */
  errors?: Record<string, string[]>
}

// ── Paginação ───────────────────────────────────────────────────────────────

export interface PagedResponse<T> {
  items: T[]
  page: number
  /** O EFETIVO, já grampeado em 200 pelo servidor. O paginador usa este. */
  pageSize: number
  totalCount: number
  totalPages: number
}

// ── Empresas ────────────────────────────────────────────────────────────────

export interface CompanyResponse {
  id: string /** = tenant_id da telemetria */
  companyCode: string /** sempre MAIÚSCULO; imutável */
  name: string
  isActive: boolean
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

export interface CreateCompanyRequest {
  companyCode: string
  name: string
}

/** Sem `companyCode`: o código é imutável por decisão do servidor. */
export interface UpdateCompanyRequest {
  name: string
  isActive: boolean
}

export interface CompanyQuery {
  page?: number
  pageSize?: number
  search?: string
  includeInactive?: boolean
  includeDeleted?: boolean
}

// ── Máquinas ────────────────────────────────────────────────────────────────

export interface MachineResponse {
  id: string /** = endpoint_id da telemetria */
  fingerprint: string /** caixa preservada; PF2X8K91 ≠ pf2x8k91 */
  displayName: string | null /** apelido do operador; null = cai para hostname/fingerprint */
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  /**
   * Isolamento de rede. Vem em `GET /machines` e `GET /machines/{id}`; é `null`
   * nas respostas de escrita (POST/PUT/PATCH), que não consultam a telemetria —
   * `null` ali é "não consultado", NUNCA "não isolada".
   */
  isolation?: MachineIsolation | null
}

/**
 * Snake_case dentro do objeto camelCase: é assim que o servidor serializa.
 *
 * Isolada ⇔ último `isolate_host` EXECUTADO > último desbloqueio CONFIRMADO
 * (`executed`/`skipped`). A regra mora no servidor; aqui só se lê `is_isolated`.
 */
export interface MachineIsolation {
  is_isolated: boolean
  /** Relógio do agente. `null` quando não está isolada. */
  isolated_since: string | null
  last_isolation_at: string | null
  last_release_at: string | null
  /** Comando mais recente, de qualquer tipo. Só no detalhe; `null` na listagem. */
  last_command: AgentCommand | null
}

/** Corpo de `POST /machines/{id}/commands/release-host`. */
export interface ReleaseHostRequest {
  /** Obrigatório: trilha de auditoria de uma descontenção. */
  reason: string
  /** Uma por CLIQUE: repetir a mesma chave devolve o comando original. */
  idempotencyKey?: string
  /** 60 a 3600; omitido = 900 (15 min). */
  expiresInSeconds?: number
}

/**
 * Corpo de `POST /machines/{id}/commands/uninstall`. Só para REEMITIR: revogar
 * licença, remover máquina ou empresa já emitem a desinstalação no Athena.
 */
export interface UninstallAgentRequest {
  /** Obrigatório: a máquina deixa de ser monitorada e só volta com instalação nova. */
  reason: string
}

export const RELEASE_TTL_MIN_SECONDS = 60
export const RELEASE_TTL_MAX_SECONDS = 3600
export const RELEASE_TTL_DEFAULT_SECONDS = 900

// ── Regime de isolamento (README do Athena §4.9) ────────────────────────────
//
// Respostas em snake_case, corpos de PEDIDO em camelCase — como o `release-host`.

/**
 * Corpo de `POST /machines/{id}/commands/isolate-host`. Staff; vale em qualquer
 * regime. Divide a chave de deduplicação com o `release-host`: um aberto recusa
 * o outro (`409 agent_command_already_open`).
 */
export interface IsolateHostRequest {
  /** Obrigatório: vai ao agente (`IsolateHost.reason`) e fica no registro dele. */
  reason: string
  /** Uma por CLIQUE: repetir a mesma chave devolve o comando original. */
  idempotencyKey?: string
  /** 60 a 3600; omitido = 900 (15 min). */
  expiresInSeconds?: number
}

/** O regime só governa a decisão AUTOMÁTICA do agente num alerta crítico. */
export type IsolationPolicyMode = 'automatic' | 'recommend' | 'disabled'

/** Um regime pronto para o console: `{ mode, label }`. `mode` aberto por tolerância. */
export interface IsolationPolicyModeInfo {
  mode: IsolationPolicyMode | (string & {})
  /** `Automático`, `Só recomendar` ou `Desligado`. */
  label: string
}

/** Por que o pedido ainda não está confirmado. Aberto: valor novo vai como veio. */
export type IsolationPolicyDivergence =
  | 'pending'
  | 'delivered'
  | 'failed'
  | 'expired'
  | 'cancelled'
  | 'agent_changed'
  | (string & {})

/** `GET /machines/{id}/isolation-policy` (staff). */
export interface MachineIsolationPolicy {
  endpoint_id: string
  /**
   * O regime EM VIGOR até onde o Athena sabe: o confirmado pelo agente ou, sem
   * confirmação, `recommend`. É o único que a tela afirma — nunca o `desired`.
   */
  effective: IsolationPolicyModeInfo
  /** Ninguém definiu nem o agente confirmou: "Só recomendar (padrão)". */
  is_default: boolean
  /** O último pedido, ou `null`. Pedido não é vigente. */
  desired: IsolationPolicyModeInfo | null
  /** O `set_isolation_policy` desse pedido, ou `null`. */
  desired_command: AgentCommand | null
  confirmed: IsolationPolicyModeInfo | null
  confirmed_at: string | null
  /** O último pedido foi confirmado (ou não há pedido). */
  in_sync: boolean
  /** `null` quando `in_sync`. */
  divergence: IsolationPolicyDivergence | null
  /** Padrão da empresa para máquinas NOVAS. Informativo. */
  company_default: IsolationPolicyModeInfo | null
  /** Os regimes que o console oferece, na ordem. */
  options: IsolationPolicyModeInfo[]
}

/** Corpo de `PUT /machines/{id}/isolation-policy`. Só `platform_admin`. */
export interface SetIsolationPolicyRequest {
  mode: IsolationPolicyMode
  reason?: string
  /** Omitido = 604800 (7 dias). */
  expiresInSeconds?: number
}

/** `GET`/`PUT /companies/{id}/isolation-policy`. Vale só para agentes que se registrarem DEPOIS. */
export interface CompanyIsolationPolicy {
  company_id: string
  /** `null` = sem padrão: os agentes novos só recomendam. */
  mode: IsolationPolicyModeInfo | null
  set_by: string | null
  set_at: string | null
  options: IsolationPolicyModeInfo[]
}

/** Corpo de `PUT /companies/{id}/isolation-policy`. `null` remove o padrão. */
export interface SetCompanyIsolationPolicyRequest {
  mode: IsolationPolicyMode | null
}

// ── Firewall de IP do host ──────────────────────────────────────────────────
//
// Respostas em snake_case (como o `release-host`), corpos de PEDIDO em camelCase.
// Contrato: Athena/.claude/docs/HOST_IP_FIREWALL.md.

/** `automatic` = o agente bloqueou sozinho (`by` = id da regra); `operator` = `by` é o UUID do operador. */
export type IpOrigin = 'automatic' | 'operator'

export interface BlockedIp {
  ip: string
  origin: IpOrigin
  /** Id da regra (automatic) OU UUID do operador (operator). */
  by: string
  since: string
  reason: string | null
  command_id: string | null
}

export interface AllowedNetwork {
  cidr: string
  origin: 'operator'
  by: string
  since: string
  note: string | null
  command_id: string | null
}

/** IP que um operador desbloqueou: o agente NÃO volta a bloqueá-lo sozinho. */
export interface LearnedIp {
  ip: string
  origin: 'operator'
  by: string
  since: string
  reason: string | null
  command_id: string | null
}

/**
 * As três listas por máquina, DERIVADAS dos fatos que o Athena viu. `scope` diz
 * isso: ações da linha de comando local do agente não chegam ao servidor.
 * Comando pendente NÃO aparece aqui.
 */
export interface MachineIpLists {
  endpoint_id: string
  blocked: BlockedIp[]
  allowlist: AllowedNetwork[]
  learned: LearnedIp[]
  scope: 'seen_by_athena'
}

export interface FleetIpItem {
  endpoint_id: string
  command: AgentCommand | null
  error_code: string | null
  error_message: string | null
}

/** Resultado POR MÁQUINA: `failed > 0` não é erro da operação inteira. */
export interface FleetIpCommandResponse {
  company_id: string
  kind: 'block_ip' | 'allow_ip'
  target: string
  queued: number
  failed: number
  items: FleetIpItem[]
}

interface IpCommandOptions {
  /** Uma por CLIQUE: repetir a mesma chave devolve o comando original. */
  idempotencyKey?: string
  /** 60 a 3600; omitido = 900. */
  expiresInSeconds?: number
}

export interface BlockIpRequest extends IpCommandOptions {
  ip: string
  /** Obrigatório. */
  reason: string
}

export interface UnblockIpRequest extends IpCommandOptions {
  ip: string
  reason?: string
}

export interface AllowIpRequest extends IpCommandOptions {
  cidr: string
  /** Obrigatória. */
  note: string
}

export interface RevokeAllowIpRequest extends IpCommandOptions {
  cidr: string
  reason?: string
}

export interface CompanyBlockIpRequest {
  ip: string
  reason: string
  expiresInSeconds?: number
}

export interface CompanyAllowIpRequest {
  cidr: string
  note: string
  expiresInSeconds?: number
}

// ── Web Defense ─────────────────────────────────────────────────────────────
//
// Mesma convenção do firewall de IP: respostas em snake_case, envelope de página
// camelCase. Contrato: Athena/HERMES_WEB_DEFENSE.md. Os comandos vão pela emissão
// genérica (`admin.enqueueCommand`), com `params` em string JSON.

/**
 * `pending` até a primeira destas: resposta do agente ao `resolve_challenge`
 * (`resolved`), alerta `block_ip` do mesmo IP na mesma máquina (`escalated`) ou
 * 24 h (`expired`). Calculado pelo servidor a cada leitura. Aberto.
 */
export type WebChallengeState = 'pending' | 'resolved' | 'escalated' | 'expired' | (string & {})

/** Estado do IP no resumo de 60 s mais recente. Aberto. */
export type WebIpState = 'observado' | 'limitado' | 'challenge' | 'bloqueado' | (string & {})

/**
 * As repetições do IP desde a abertura, somadas dos resumos de 60 s. É uma
 * APROXIMAÇÃO: o agente só resume os 20 IPs mais ativos de cada janela.
 * `last_seen_at: null` = nenhum resumo ainda, e então os contadores vêm 0 —
 * um zero que não pode ser lido como "nenhuma requisição".
 */
export interface WebChallengeActivity {
  requests: number
  /** Respondidas com 403. */
  blocked: number
  /** Respondidas com 429. */
  limited: number
  /** Atendidas sob challenge. */
  challenged: number
  state: WebIpState | null
  last_seen_at: string | null
}

/** A resposta do agente ao PRIMEIRO `resolve_challenge` que fechou o item. */
export interface WebChallengeResolution {
  command_id: string
  decision: 'allow' | 'deny' | (string & {})
  /** `null` num `deny`. */
  scope: 'ip' | 'rule_route' | null | (string & {})
  outcome: 'executed' | 'skipped' | 'failed' | (string & {})
  detail: string | null
  finished_at: string
  requested_by: string
}

/**
 * Só no detalhe. `query` e `payload` já vêm mascarados pelo agente (senha, token,
 * CPF, cartão) e cortados em 512 bytes: texto de atacante, exibido como texto.
 */
export interface WebChallengeEvidence {
  query: string | null
  payload: string | null
  user_agent: string | null
  /** `$request_id` do nginx. */
  request_id: string | null
  /** Risco acumulado do IP quando o challenge abriu. */
  risk_points: number | null
  /** `"tokens_sql (+0.95)"`. Vazia enquanto não há modelo web treinado. */
  contributions: string[]
  /** `data.explanation` da regra, como o agente mandou. Leia por `decodeExplanation`. */
  explanation: unknown[] | null
}

export interface WebChallenge {
  /** Máquina DO ALERTA: o `challenge_id` só existe nela, e o `resolve_challenge` vai para ela. */
  endpoint_id: string
  /** Apelido ou hostname. */
  machine_name: string | null
  tenant_id: string
  /** Opaco. */
  challenge_id: string
  ip: string
  state: WebChallengeState
  opened_at: string
  /** `opened_at` + 24 h. A contagem regressiva usa este com o relógio local. */
  expires_at: string
  /** Do servidor, na hora da consulta; 0 fora de `pending`. */
  expires_in_seconds: number
  /** `null` enquanto pendente. */
  closed_at: string | null
  rule_id: string | null
  rule_name: string | null
  severity: string | null
  /** 0 a 100. */
  risk_score: number | null
  method: string | null
  /** Cabeçalho `Host`: o site atacado, não a máquina. */
  host: string | null
  path: string | null
  activity: WebChallengeActivity
  resolution: WebChallengeResolution | null
  /** O `block_ip` automático que fechou o item. */
  escalation: { blocked_at: string; detail: string | null } | null
  /** Sempre `null` na fila; preenchido no detalhe. */
  evidence: WebChallengeEvidence | null
}

export interface WebChallengeQuery {
  /** `pending` (padrão, a fila) ou `all` (histórico). */
  state?: 'pending' | 'all'
  companyId?: string
  endpointId?: string
  ip?: string
  page?: number
  pageSize?: number
}

export interface WebException {
  id: string
  rule: string | null
  route: string | null
  /** O que o agente guardou: o operador que criou. */
  by: string | null
  since: string | null
  note: string | null
}

/**
 * A última listagem que o agente devolveu, MENOS as exceções que um
 * `revoke_web_exception` confirmado depois dela já tirou.
 */
export interface WebDefenseExceptions {
  endpoint_id: string
  /** O `list_web_exceptions` que trouxe a lista. */
  command_id: string
  /** Quando o agente listou. */
  as_of: string
  exceptions: WebException[]
  /** As que não couberam no resultado do comando. A lista inteira está no artefato `web_defense`. */
  omitted: number
  /** Revogadas depois da listagem (já fora de `exceptions`). */
  revoked_since: string[]
}

/** Artefato coletado do agente (snake_case). Só `platform_admin` lê. */
export interface ArtifactSummary {
  id: string
  command_id: string
  endpoint_id: string
  tenant_id: string
  /** `system_snapshot`, `event_log`, `memory_dump`, `file`, … Aberto. */
  artifact: string
  content_type: string
  size_bytes: number
  sha256: string
  collected_at: string
  received_at: string
}

/** JSON em `content`, text/plain em `text`; binário não vem no corpo. */
export interface ArtifactDetail {
  summary: ArtifactSummary
  content: unknown
  text: string | null
}

export interface ArtifactQuery {
  artifact?: string
  page?: number
  pageSize?: number
}

export interface MachineCommandQuery {
  page?: number
  pageSize?: number
  kind?: string
  openOnly?: boolean
}

export interface CreateMachineRequest {
  fingerprint: string
  displayName?: string | null
}

export interface UpdateMachineRequest {
  fingerprint: string
}

export interface UpdateMachineDisplayNameRequest {
  /** Vazio limpa o apelido: o rótulo volta a ser o hostname reportado. */
  displayName: string | null
}

export interface MachineQuery {
  page?: number
  pageSize?: number
  search?: string
  includeDeleted?: boolean
  /** `true` só isoladas, `false` só não isoladas; omitido = todas. */
  isolated?: boolean
  /** Só `/api/v1/machines` (Black Ghost). */
  companyId?: string
}

// ── Licenças ────────────────────────────────────────────────────────────────

export type ProductKeyStatus = 'Created' | 'Active' | 'Suspended' | 'Revoked' | 'Expired'

export const PRODUCT_KEY_STATUSES: readonly ProductKeyStatus[] = [
  'Created',
  'Active',
  'Suspended',
  'Revoked',
  'Expired',
]

export interface ProductKeyResponse {
  id: string
  productKey: string /** ATHN-XXXXX-XXXXX-XXXXX-XXXXX */
  status: ProductKeyStatus
  companyId: string
  machineId: string | null
  /** Conveniência de exibição: a licença SOZINHA autoriza. Não é a verdade final. */
  authorizesIngestion: boolean
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  lastActivationAt: string | null
}

export interface CreateProductKeyRequest {
  companyId: string
  machineId?: string | null
  activate?: boolean
}

/** PUT: substituição TOTAL — `machineId` ausente vira null e DESVINCULA. */
export interface UpdateProductKeyRequest {
  machineId: string | null
  status: ProductKeyStatus
}

export interface ActivateProductKeyRequest {
  machineId?: string | null
}

export interface ProductKeyQuery {
  page?: number
  pageSize?: number
  companyId?: string
  machineId?: string
  status?: ProductKeyStatus
  includeDeleted?: boolean
}

// ── Identidade ──────────────────────────────────────────────────────────────

export type AthenaRole = 'platform_admin' | 'platform_staff' | 'tenant_admin' | 'tenant_user'

/**
 * Escopo do usuário, e o que escolhe a experiência do Hermes: `platform` é a
 * Black Ghost (console global, sem empresa); `tenant` é uma empresa cliente.
 */
export type AthenaScope = 'platform' | 'tenant'

export const PLATFORM_ROLES: readonly AthenaRole[] = ['platform_admin', 'platform_staff']
export const TENANT_ROLES: readonly AthenaRole[] = ['tenant_admin', 'tenant_user']
export const ROLES: readonly AthenaRole[] = [...PLATFORM_ROLES, ...TENANT_ROLES]

export interface LoginRequest {
  username: string
  password: string
}

export interface ChangePasswordRequest {
  currentPassword: string
  newPassword: string
}

export interface AuthTokenResponse {
  accessToken: string /** JWT, 15 min, header Authorization */
  tokenType: 'Bearer'
  expiresIn: number
  expiresAt: string
  refreshToken: string /** opaco, 30 dias, ROTACIONA a cada uso */
  refreshExpiresAt: string
  user: UserResponse
}

export interface CurrentUserResponse {
  id: string
  username: string
  email: string
  displayName: string
  roles: AthenaRole[]
  lastLoginAt: string | null
  scope: AthenaScope
  /** `null` na Black Ghost. */
  companyId: string | null
  /** `null` na Black Ghost. */
  companyName: string | null
  /** Senha provisória ainda não trocada. */
  mustChangePassword: boolean
}

export interface SessionResponse {
  id: string
  createdAt: string
  expiresAt: string
  lastUsedAt: string
  ipAddress: string | null
  userAgent: string | null
  /** A sessão que fez ESTA chamada. Revogá-la derruba quem está olhando. */
  isCurrent: boolean
}

export interface UserResponse {
  id: string
  username: string /** minúsculo; IMUTÁVEL */
  email: string
  displayName: string
  roles: AthenaRole[]
  isActive: boolean
  /** Bloqueio temporário por 5 senhas erradas; expira sozinho em 15 min. */
  isLockedOut: boolean
  createdAt: string
  updatedAt: string
  passwordChangedAt: string
  lastLoginAt: string | null
  deletedAt: string | null
  scope: AthenaScope
  /** `null` para usuário da Black Ghost; imutável para usuário de cliente. */
  companyId: string | null
  /** Ainda com a senha provisória. */
  mustChangePassword: boolean
}

export interface CreateUserRequest {
  username: string
  email: string
  displayName?: string | null
  roles: AthenaRole[]
  /** Obrigatório com papéis `tenant_*`; proibido com `platform_*`. */
  companyId?: string
}

/** `POST /api/tenant/users`: a empresa vem do token, nunca do corpo. */
export interface CreateTenantUserRequest {
  username: string
  email: string
  displayName?: string | null
  roles: AthenaRole[]
}

/** PUT: `roles` é o conjunto FINAL, não um acréscimo. */
export interface UpdateUserRequest {
  email: string
  displayName?: string | null
  roles: AthenaRole[]
  isActive: boolean
}

export interface UserQuery {
  page?: number
  pageSize?: number
  search?: string
  role?: AthenaRole
  includeInactive?: boolean
  includeDeleted?: boolean
  /** Só `/api/v1/users` (Black Ghost). */
  companyId?: string
  scope?: AthenaScope
}

/** Comprimento mínimo de senha. É a única regra — ver NIST SP 800-63B. */
export const PASSWORD_MIN_LENGTH = 12
export const PASSWORD_MAX_LENGTH = 256
